import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";
import { nx, NX_RING_CLIP, type NxSize } from "../lib/onyx-material";

/**
 * NotchTextarea — the multi-line field in the hex-notch silhouette, sibling to
 * NotchedInput (form descriptions, longer notes). Same keyline contract: the
 * outline is an `nx-rim` ring (a real border is sliced at the notch) — `--border`
 * hairline at rest → `--ring` accent on focus → `--destructive` on `aria-invalid`
 * — plus an accent drop-shadow glow on focus. `ref` forwards to the `<textarea>`.
 */
const fieldVariants = cva(
  [
    "relative flex w-full bg-background text-foreground",
    "transition-[filter] duration-180 ease-smooth-out",
    "focus-within:[filter:drop-shadow(0_0_3px_hsl(var(--ring)/0.45))]",
    "has-[textarea[aria-invalid=true]]:focus-within:[filter:drop-shadow(0_0_3px_hsl(var(--destructive)/0.45))]",
    "has-[textarea:disabled]:opacity-50",
  ].join(" "),
  {
    variants: {
      size: {
        sm: "px-3 py-2 text-xs",
        default: "px-3 py-2.5 text-sm",
        lg: "px-4 py-3 text-base",
      },
    },
    defaultVariants: { size: "default" },
  },
);

export interface NotchTextareaProps
  extends
    Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "size">,
    VariantProps<typeof fieldVariants> {
  /** Notch chamfer size. Default `b`. */
  notch?: NxSize;
  /** Class for the outer (clipped) field box; `className` lands on the `<textarea>`. */
  wrapperClassName?: string;
}

export const NotchTextarea = React.forwardRef<HTMLTextAreaElement, NotchTextareaProps>(
  ({ className, wrapperClassName, notch = "b", size, rows = 4, ...props }, ref) => (
    <div className={cn(nx[notch], fieldVariants({ size }), wrapperClassName)}>
      <textarea
        ref={ref}
        rows={rows}
        className={cn(
          // `peer` so the ring reacts to :focus; the box owns fill + clip, the
          // textarea is transparent + borderless. Resize stays vertical-only so a
          // drag never escapes the notched silhouette.
          "peer w-full resize-y border-0 bg-transparent text-foreground outline-none",
          "placeholder:text-muted-foreground disabled:cursor-not-allowed",
          className,
        )}
        {...props}
      />
      <span
        aria-hidden
        className={cn(
          "nx-rim pointer-events-none absolute inset-0",
          "[--nx-rim:hsl(var(--border))]",
          "peer-focus:[--nx-rim:hsl(var(--ring))]",
          "peer-aria-[invalid=true]:[--nx-rim:hsl(var(--destructive))]",
        )}
        style={{ clipPath: NX_RING_CLIP[notch] }}
      />
    </div>
  ),
);

NotchTextarea.displayName = "NotchTextarea";
